import React, { Fragment } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import { trackEvent } from '../../utils/analytics';
import Indicators, { INDICATOR_CATEGORIES } from '../../data/indicators';

const ClickHandler = () => window.scrollTo(10, 0);
const navy = '#020842';
const blue = '#0044EB';
const gold = '#C77F12';

const catLabel = (key) => (INDICATOR_CATEGORIES.find((c) => c.key === key) || {}).label || 'Indicator';

const IndicatorSinglePage = () => {
    const { slug } = useParams();
    const ind = Indicators.find((i) => i.slug === slug);

    usePageSeo({
        title: ind ? `${ind.shortName} — Free Futures Indicator` : 'Indicator not found',
        description: ind
            ? `${ind.summary.split('. ')[0]}. Free source for TradingView (Pine Script v6)${ind.tradovate ? ' and Tradovate' : ''}, with a how-to guide.`
            : 'BuildLab futures indicators.',
        noindex: !ind,
    });

    if (!ind) return <Navigate to="/indicators" replace />;
    if (ind.link) return <Navigate to={ind.link} replace />;

    const heroImg = ind.image || `/images/indicators/${ind.slug}.svg`;

    const onDownload = (platform) =>
        trackEvent('indicator_download', { indicator: ind.shortName, platform });

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={ind.shortName} pagesub={'Indicator'} pageTop={'Indicators'} />

                {heroImg && (
                    <section style={{ background: '#fff', paddingTop: 28 }}>
                        <div className="container">
                            <img src={heroImg} alt={`${ind.shortName} — BuildLab indicator (illustrative)`} style={{ width: '100%', borderRadius: 16, display: 'block', boxShadow: '0 18px 50px rgba(2,8,66,.18)' }} loading="lazy" />
                        </div>
                    </section>
                )}

                {/* HERO + DOWNLOADS */}
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row align-items-start">
                            <div className="col-lg-7">
                                <div style={{ fontSize: '.74rem', fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: gold }}>{catLabel(ind.category)}</div>
                                <h2 className="mt-2 mb-3" style={{ color: navy }}>{ind.tagline}</h2>
                                <p>{ind.summary}</p>
                                <p className="mb-0"><b style={{ color: navy }}>Best for:</b> {ind.bestFor}</p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
                                    {ind.platforms.map((p) => (
                                        <span key={p} style={{ fontSize: '.74rem', fontWeight: 600, color: blue, background: 'rgba(0,68,235,.08)', borderRadius: 20, padding: '4px 12px' }}>{p}</span>
                                    ))}
                                    <span style={{ fontSize: '.74rem', fontWeight: 600, color: '#1f2937', background: '#eef0f6', borderRadius: 20, padding: '4px 12px' }}>Plots in: {ind.area}</span>
                                </div>
                            </div>

                            <div className="col-lg-5 mt-4 mt-lg-0">
                                <div style={{ background: navy, borderRadius: 16, padding: '28px 30px', color: '#fff' }}>
                                    <div style={{ fontSize: '.76rem', fontWeight: 700, letterSpacing: '.08em', color: '#F3A338' }}>FREE · FULL SOURCE</div>
                                    <div style={{ fontSize: '1.35rem', fontWeight: 800, margin: '6px 0 14px', lineHeight: 1.2 }}>Download the indicator</div>
                                    <a href={ind.pine} download onClick={() => onDownload('TradingView')} className="btn btn-primary" style={{ width: '100%', marginBottom: 12 }}>
                                        <span className="btn_label" data-text="TradingView (.pine)">TradingView (.pine)</span>
                                        <span className="btn_icon"><i className="fa-solid fa-download" aria-hidden="true"></i></span>
                                    </a>
                                    {ind.tradovate ? (
                                        <a href={ind.tradovate} download onClick={() => onDownload('Tradovate')} className="btn btn-outline-light" style={{ width: '100%', color: '#fff', borderColor: 'rgba(255,255,255,.5)' }}>
                                            <span className="btn_label" data-text="Tradovate (.js)">Tradovate (.js)</span>
                                            <span className="btn_icon"><i className="fa-solid fa-download" aria-hidden="true"></i></span>
                                        </a>
                                    ) : (
                                        <div style={{ fontSize: '.82rem', color: 'rgba(255,255,255,.72)', border: '1px dashed rgba(255,255,255,.3)', borderRadius: 8, padding: '9px 12px', textAlign: 'center' }}>Tradovate version — coming soon</div>
                                    )}
                                    <p style={{ fontSize: '.8rem', opacity: .8, margin: '14px 0 0' }}>
                                        Open it, read it, edit it — it’s yours. © BuildLab Consulting.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* HOW TO USE */}
                <section className="section_space">
                    <div className="container">
                        <h2 className="mb-4" style={{ color: navy }}>How to use it</h2>
                        <div className="row">
                            {ind.howToUse.map((h, i) => (
                                <div className="col-lg-6 mb-4" key={i}>
                                    <div style={{ borderLeft: `3px solid ${blue}`, padding: '4px 0 4px 18px' }}>
                                        <h3 className="h6 mb-1" style={{ color: navy }}>{h.t}</h3>
                                        <p className="mb-0" style={{ fontSize: '.95rem' }}>{h.d}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* INPUTS + INSTALL */}
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-6 mb-4 mb-lg-0">
                                <h2 className="mb-3" style={{ color: navy }}>Settings</h2>
                                <ul className="list-unstyled m-0">
                                    {ind.inputs.map((inp, i) => (
                                        <li key={i} style={{ padding: '10px 0', borderBottom: '1px solid #e7e9f2' }}>
                                            <b style={{ color: navy }}>{inp.name}</b>
                                            <div style={{ fontSize: '.92rem' }}>{inp.desc}</div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="col-lg-6">
                                <h2 className="mb-3" style={{ color: navy }}>Install</h2>
                                <p className="mb-2"><b>TradingView:</b> open the Pine Editor, paste the <code>.pine</code> source, click Save, then “Add to chart.”</p>
                                {ind.tradovate && <p className="mb-3"><b>Tradovate:</b> Chart ▸ Indicators ▸ “+” (Add Custom Indicator), paste the <code>.js</code> source, Save, then add it to the chart.</p>}
                                {ind.notes && (
                                    <div style={{ background: '#fff', border: '1px solid #e7e9f2', borderRadius: 12, padding: '14px 16px' }}>
                                        <b style={{ color: navy }}>Note:</b> <span style={{ fontSize: '.92rem' }}>{ind.notes}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="mt-4">
                            <Link onClick={ClickHandler} to="/indicators" style={{ color: blue, fontWeight: 600 }}>← All BuildLab indicators</Link>
                        </div>
                    </div>
                </section>

                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default IndicatorSinglePage;
